import { range } from 'lodash';
import {
  AvailableBlockDentistForSplitScheduling,
  AvailableBlockOperatory,
  SearchingService,
} from 'interfaces/timeslotType';

const EXAM_PLACEMENT = {
  HEAD: 'head',
  TAIL: 'tail',
};

export interface DentistInfo {
  id: string | null;
  timeSlot: number | null;
  timeSlotType: string | null;
}

export const getOperatoryId = ({
  date,
  doctorId,
  timeSlot,
  doctors,
}: {
  date: string;
  doctorId: string;
  timeSlot: number;
  doctors: {
    id: string;
    availableBlocks: {
      date: string;
      availableBlockOperatories?: AvailableBlockOperatory[];
    }[];
  }[];
}) => {
  const selectedDoctor = doctors.find((item: any) => item.id === doctorId);

  const availableBlocksByDate =
    selectedDoctor?.availableBlocks.find((item: any) => item.date === date) ||
    null;

  const availableBlockOperatoriesByDate =
    availableBlocksByDate?.availableBlockOperatories || [];

  const operatoryIds = availableBlockOperatoriesByDate
    .filter((item: any) => item.blocks.includes(timeSlot))
    .map((item: any) => item.operatoryId);

  return operatoryIds[0] || '';
};

const isFirstContainedBySecondBlockNumbers = (
  firstBlockNumbers: number[],
  secondBlockNumbers: number[]
) => {
  return firstBlockNumbers.every((item) => secondBlockNumbers.includes(item));
};

const hasExistingHead = ({
  dentists,
  timeSlot,
  doctorDuration,
}: {
  dentists: AvailableBlockDentistForSplitScheduling[];
  timeSlot: number;
  doctorDuration: number;
}) => {
  const headRange = range(timeSlot, timeSlot + doctorDuration);

  const isExistingHead = dentists.some((item) =>
    isFirstContainedBySecondBlockNumbers(
      headRange,
      item.headAvailableBlockNumbers
    )
  );

  return isExistingHead;
};

const hasExistingTail = ({
  dentists,
  timeSlot,
  doctorDuration,
  hygienistDuration,
}: {
  dentists: AvailableBlockDentistForSplitScheduling[];
  timeSlot: number;
  doctorDuration: number;
  hygienistDuration: number;
}) => {
  const tailRange = range(
    timeSlot + hygienistDuration,
    timeSlot + hygienistDuration + doctorDuration
  );

  const isExistingTail = dentists.some((item) =>
    isFirstContainedBySecondBlockNumbers(
      tailRange,
      item.tailAvailableBlockNumbers
    )
  );

  return isExistingTail;
};

const chooseRelevantExamPlacement = ({
  timeSlot,
  isExistingHead,
  isExistingTail,
  selectedDentistId,
}: {
  timeSlot: number;
  isExistingHead: boolean;
  isExistingTail: boolean;
  selectedDentistId: string | null;
}) => {
  if (isExistingHead && isExistingTail) {
    return {
      id: selectedDentistId,
      timeSlot,
      timeSlotType: null,
    };
  }

  if (isExistingHead && !isExistingTail) {
    return {
      id: selectedDentistId,
      timeSlot,
      timeSlotType: EXAM_PLACEMENT.HEAD,
    };
  }

  if (isExistingTail && !isExistingHead) {
    return {
      id: selectedDentistId,
      timeSlot,
      timeSlotType: EXAM_PLACEMENT.TAIL,
    };
  }

  return {
    id: selectedDentistId,
    timeSlot,
    timeSlotType: null,
  };
};

export const getDentistIdForSplitScheduling = ({
  date,
  doctorId,
  selectedDentistId,
  timeSlot,
  doctors,
}: {
  date: string;
  doctorId: string | null;
  selectedDentistId: string | null;
  timeSlot: number;
  doctors: {
    id: string;
    availableBlocks: {
      date: string;
      searchingService: SearchingService;
      availableBlockOperatories?: AvailableBlockOperatory[];
      dentists?: AvailableBlockDentistForSplitScheduling[];
    }[];
  }[];
}) => {
  if (doctorId === null) {
    const isExistingHead = doctors.some((doctor) => {
      const availableBlock = doctor.availableBlocks.find(
        (item) => item.date === date
      );

      const searchingService = availableBlock?.searchingService;

      const doctorDuration = searchingService?.doctorDuration || 0;

      let dentists = availableBlock?.dentists || [];

      if (selectedDentistId) {
        dentists = dentists.filter(
          (item) => item.dentistId === selectedDentistId
        );
      }

      const isExistingHead = hasExistingHead({
        dentists,
        timeSlot,
        doctorDuration,
      });

      return isExistingHead;
    });

    const isExistingTail = doctors.some((doctor) => {
      const availableBlock = doctor.availableBlocks.find(
        (item) => item.date === date
      );

      const searchingService = availableBlock?.searchingService;

      const doctorDuration = searchingService?.doctorDuration || 0;

      const hygienistDuration = searchingService?.hygienistDuration || 0;

      const dentists = availableBlock?.dentists || [];

      const isExistingTail = hasExistingTail({
        dentists,
        timeSlot,
        doctorDuration,
        hygienistDuration,
      });

      return isExistingTail;
    });

    return chooseRelevantExamPlacement({
      timeSlot,
      isExistingHead,
      isExistingTail,
      selectedDentistId,
    });
  } else {
    const doctor = doctors.find((item) => item.id === doctorId);

    if (!doctor) {
      return {
        id: null,
        timeSlot,
        timeSlotType: null,
      };
    }

    const availableBlock = doctor.availableBlocks.find(
      (item) => item.date === date
    );

    const searchingService = availableBlock?.searchingService;

    const doctorDuration = searchingService?.doctorDuration || 0;

    const hygienistDuration = searchingService?.hygienistDuration || 0;

    let dentists = availableBlock?.dentists || [];

    if (selectedDentistId) {
      dentists = dentists.filter(
        (item) => item.dentistId === selectedDentistId
      );
    }

    const isExistingHead = hasExistingHead({
      dentists,
      timeSlot,
      doctorDuration,
    });

    const isExistingTail = hasExistingTail({
      dentists,
      timeSlot,
      doctorDuration,
      hygienistDuration,
    });

    return chooseRelevantExamPlacement({
      timeSlot,
      isExistingHead,
      isExistingTail,
      selectedDentistId,
    });
  }
};
